import React from 'react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Database, Calendar, Clock } from 'lucide-react';

const ArticleLiDAR = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Resources", url: "/resources" },
    { name: "LiDAR Annotation", url: "/resources/lidar-annotation" }
  ];
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "LiDAR Point Cloud Annotation: Best Practices for Autonomous Systems",
    "description": "Master LiDAR annotation techniques for autonomous vehicles. Expert guidance on 3D bounding boxes, segmentation, and quality assurance.",
    "datePublished": "2024-12-12",
    "author": { "@type": "Organization", "name": "Orboroi" },
    "publisher": { "@type": "Organization", "name": "Orboroi", "logo": { "@type": "ImageObject", "url": `${siteUrl}/logo.png` } },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/resources/lidar-annotation` }
  };
  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO title="LiDAR Point Cloud Annotation Best Practices" description="Master LiDAR annotation for autonomous vehicles. Expert guidance on 3D bounding boxes, segmentation, and quality assurance for production AI." canonical="/resources/lidar-annotation" type="article" author="Orboroi Team" datePublished="2024-12-12" structuredData={structuredData} breadcrumbs={breadcrumbs} />
      <div className="container mx-auto px-6 py-6">
        <Link to="/resources">
          <Button variant="ghost" className="text-[#379F7F] hover:bg-[#379F7F]/10">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Button>
        </Link>
      </div>

      <section className="py-8 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Database className="w-8 h-8 text-[#379F7F]" />
              <span className="text-[#379F7F] font-semibold">Data Preparation</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              LiDAR Annotation for Autonomous Systems
            </h1>
            
            <div className="flex items-center gap-6 text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Nov 15, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>12 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <article className="prose prose-invert prose-lg max-w-none">
              
              <div className="mb-12">
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  LiDAR (Light Detection and Ranging) has become essential for autonomous vehicles, robotics, and AR/VR 
                  applications. But raw 3D point cloud data is meaningless without accurate annotation—and annotating 
                  3D data presents unique challenges compared to 2D image labeling.
                </p>
                <p className="text-lg text-gray-400 leading-relaxed">
                  This technical deep-dive explores best practices for 3D point cloud annotation, sensor fusion workflows, 
                  and quality assurance techniques for production autonomous systems.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Understanding LiDAR Data</h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  LiDAR sensors emit laser pulses and measure their reflection to create 3D point clouds. Each point 
                  contains X, Y, Z coordinates plus additional attributes:
                </p>
                
                <ul className="space-y-3 mb-6">
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Intensity:</strong> Reflectivity of the surface hit by the laser</span>
                  </li>
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Timestamp:</strong> Precise time when the measurement was taken</span>
                  </li>
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Return Number:</strong> Which reflection this is (first, second, etc.)</span>
                  </li>
                </ul>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Annotation Types for Autonomous Driving</h2>
                
                <div className="space-y-6">
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-[#379F7F] mb-3">3D Bounding Boxes</h3>
                    <p className="text-gray-300 mb-3">
                      The most common annotation type for object detection. Annotators draw cuboids around objects with:
                    </p>
                    <ul className="space-y-2">
                      <li className="text-gray-300">Position (X, Y, Z center coordinates)</li>
                      <li className="text-gray-300">Dimensions (Length, Width, Height)</li>
                      <li className="text-gray-300">Orientation (Yaw angle for object heading)</li>
                      <li className="text-gray-300">Class label (Car, Pedestrian, Cyclist, etc.)</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-[#379F7F] mb-3">Semantic Segmentation</h3>
                    <p className="text-gray-300">
                      Assigning a class label to each point in the cloud. Essential for understanding drivable surfaces, 
                      obstacles, and scene context. More time-intensive but provides richer ground truth.
                    </p>
                  </div>
                  
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-[#379F7F] mb-3">Object Tracking</h3>
                    <p className="text-gray-300">
                      Linking the same object across sequential frames with unique IDs. Critical for predicting object 
                      trajectories and planning vehicle behavior.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Sensor Fusion: Combining LiDAR with Cameras</h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  Production autonomous systems rarely use LiDAR alone. Sensor fusion combines 3D LiDAR data with 2D 
                  camera images for more robust perception:
                </p>
                
                <div className="border-l-4 border-[#379F7F] pl-6 py-2 mb-6">
                  <h4 className="text-xl font-bold text-white mb-2">Annotation Workflow</h4>
                  <p className="text-gray-300 mb-3">
                    Annotators work with both data sources simultaneously:
                  </p>
                  <ul className="space-y-2">
                    <li className="text-gray-300">1. Start with 3D LiDAR bounding boxes for spatial accuracy</li>
                    <li className="text-gray-300">2. Project boxes onto corresponding camera images</li>
                    <li className="text-gray-300">3. Refine using visual information (object type, occlusions)</li>
                    <li className="text-gray-300">4. Validate consistency between sensors</li>
                  </ul>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Quality Assurance for 3D Annotation</h2>
                
                <h3 className="text-2xl font-bold text-white mb-4">Common Errors to Check:</h3>
                <ul className="space-y-3 mb-6">
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Floating boxes:</strong> Bounding boxes not aligned with ground plane</span>
                  </li>
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Incomplete coverage:</strong> Boxes that don't fully contain objects</span>
                  </li>
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Rotation errors:</strong> Incorrect object orientation</span>
                  </li>
                  <li className="text-lg text-gray-300 flex items-start">
                    <span className="text-[#379F7F] mr-3 mt-1">•</span>
                    <span><strong className="text-white">Missing objects:</strong> Especially partially occluded ones</span>
                  </li>
                </ul>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Tooling and Infrastructure</h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  Effective LiDAR annotation requires specialized tools with:
                </p>
                
                <div className="bg-gray-800 border border-[#379F7F]/30 rounded-lg p-8 mb-6">
                  <ul className="space-y-3">
                    <li className="text-gray-300">
                      <strong className="text-white">3D Visualization:</strong> Interactive point cloud viewers with 
                      rotation, zoom, and filtering
                    </li>
                    <li className="text-gray-300">
                      <strong className="text-white">Multi-View Display:</strong> Simultaneous bird's-eye, side, and 
                      front views for accurate annotation
                    </li>
                    <li className="text-gray-300">
                      <strong className="text-white">Sensor Fusion UI:</strong> Synchronized display of LiDAR and camera data
                    </li>
                    <li className="text-gray-300">
                      <strong className="text-white">Automated QA:</strong> Scripts that flag geometric inconsistencies 
                      and missing annotations
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  LiDAR annotation is technically complex, requiring specialized expertise, tools, and quality processes. 
                  Success depends on understanding 3D geometry, sensor characteristics, and autonomous system requirements.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Organizations building autonomous systems must invest in production-grade 3D annotation capabilities—either 
                  building internal teams or partnering with specialized providers who understand these unique challenges.
                </p>
              </div>

              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 mt-12">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Production-Grade LiDAR Annotation
                </h3>
                <p className="text-gray-300 mb-6">
                  Orboroi specializes in 3D point cloud annotation and sensor fusion for autonomous systems, delivering 
                  production-ready training data.
                </p>
                <Link to="/contact">
                  <Button className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold">
                    Talk to a Data Expert
                  </Button>
                </Link>
              </div>

            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleLiDAR;
